import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {FileSelectorFolderFilter} from 'file-browser/FileSelectorConstants';
import {LocationInformation} from 'molbio/components/SaveLocationPicker';
import {DisplayInfo} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

export interface HomologyDetectionOptions {
  maxRegionLength: number;
  slot1Index: number;
  slot2Index: number;
}

export interface HomologyDetectionResponse {
  bases: string | null;
  basesSecondary?: string;
}

export interface BulkAssemblyNewWorklistInfo {
  name: string;
}

export interface AssemblyMetaInfo {
  activeSlotIndex: number;
  state: string;
  summary: Summary | null;
}

export enum BulkAssemblyOutputType {
  ASSEMBLED_SEQUENCES = 'ASSEMBLED_SEQUENCES',
  GIBSON_PRIMERS = 'GIBSON_PRIMERS',
  FRAGMENTS = 'FRAGMENTS',
}

export const BULK_ASSEMBLY_OUTPUT_TYPE_TO_DISPLAY_STRING: {[key in BulkAssemblyOutputType]: string} = {
  [BulkAssemblyOutputType.ASSEMBLED_SEQUENCES]: 'assembled sequences',
  [BulkAssemblyOutputType.GIBSON_PRIMERS]: 'primers',
  [BulkAssemblyOutputType.FRAGMENTS]: 'fragments',
};

export interface GibsonParameters {
  tmAnneal: number;
  tmWhole: number;
  tmDiff: number;
  minOverlap: number;
  maxOverlap: number;
}

export interface AssemblyWithOptionalMeta {
  id: string;
  dataSources: Array<DataSource>;
  meta?: AssemblyMetaInfo;
  slots: Array<Slot>;
  tagSchemaId?: string | null;
  topology: SequenceTopology;
  // User can optionally add assembled sequences to an existing or new worklist.
  // Within each LocationInformation.worklistParams, worklistFormType keeps track of the form state, and
  // either targetWorklistId (meaning an existing worklist) or newWorklistInfo can be present, but not both.
  sequenceSaveInformation: LocationInformation;
  primerSaveInformation: LocationInformation;
  fragmentSaveInformation: LocationInformation;
}

export interface Assembly extends AssemblyWithOptionalMeta {
  meta: AssemblyMetaInfo;
}

// Assembly is what is serialized and sent to the server,
// LocalAssembly is what is used elsewhere on the frontend
export interface LocalAssembly extends Assembly {
  meta: AssemblyMetaInfo;
  dataSources: Array<LocalDataSource>;
}

export interface SequenceSummary {
  error: string | null;
  fivePrimeOverlap: string;
  fivePrimeSticky: string;
  length: number;
  name: string;
  threePrimeOverlap: string;
  threePrimeSticky: string;
  start: number;
  end: number;
  sequenceLength: number;
  strand: StrandOfDoubleStranded;
  circular: boolean;
  fivePrimeDisplayInfo?: DisplayInfo;
  threePrimeDisplayInfo?: DisplayInfo;
}

export interface SlotSummary {
  errors: Array<string>;
  sequences: Array<SequenceSummary>;
  warnings: Array<string>;
  gibsonStatus?: GibsonStatus;
}

export interface DataSource {
  id: string;
  fileIds?: Array<string>;
  sequenceIds?: Array<string>;
}

export interface LocalDataSource extends DataSource {
  fileIds: Array<string>;
}

export enum DataSourceType {
  FOLDERS = 'FOLDERS',
  WORKLIST = 'WORKLIST',
  OTHER_SLOT = 'OTHER_SLOT',
}

export interface Slot {
  dataSourceId: string | undefined;
  dataSourceType: DataSourceType;
  slotParams: {
    endpoints: Array<Endpoint>;
    schemaFieldId: string | null;
    name: string;
    useLargerFragment: boolean | undefined;
    useReverse: boolean;
  };
}

export enum GibsonStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  UNFINALIZED = 'UNFINALIZED',
  PENDING_ADJACENT = 'PENDING_ADJACENT',
}

export interface Summary {
  numOutputSequences: number;
  numPrimerPairs: number | null;
  slots: Array<SlotSummary>;
  errors?: Array<string>;
}

export interface Sequence {
  fivePrimeSticky: string;
  fivePrimeOverlap: string;
  threePrimeSticky: string;
  threePrimeOverlap: string;
  error: string | null;
}

export interface EnzymeEndpoint {
  type: EndpointType.ENZYME;
  enzymeId: number | null;
}

export interface HomologyEndpoint {
  type: EndpointType.HOMOLOGY;
  bases: string;
}

interface BasesEndpoint {
  bases: string;
  position: null;
}

export interface BasesConcatenationEndpoint extends BasesEndpoint {
  type: EndpointType.CONCATENATION;
}

interface BasesGibsonEndpoint extends BasesEndpoint {
  type: EndpointType.GIBSON;
}

interface PositionEndpoint {
  bases: null;
  position: number;
}

interface PositionConcatenationEndpoint extends PositionEndpoint {
  type: EndpointType.CONCATENATION;
}

interface PositionGibsonEndpoint extends PositionEndpoint {
  type: EndpointType.GIBSON;
}

export type ConcatenationEndpoint = BasesConcatenationEndpoint | PositionConcatenationEndpoint;

export type GibsonEndpoint = BasesGibsonEndpoint | PositionGibsonEndpoint;

export type Endpoint = EnzymeEndpoint | HomologyEndpoint | ConcatenationEndpoint | GibsonEndpoint;

export const BULK_ASSEMBLY_SCREEN = 'BULK_ASSEMBLY_SCREEN';

export const BULK_ASSEMBLY_STATE = {
  UNASSEMBLED: 'UNASSEMBLED', // Initial state
  VALIDATING: 'VALIDATING', // After any edit is made, server validation is triggered
  VALIDATED: 'VALIDATED', // After server validation finishes (EVEN if errors are returned to the user)
  UNVALIDATED: 'UNVALIDATED', // Server validation failed to run (e.g. network error / 500)
  ASSEMBLING: 'ASSEMBLING', // From VALIDATED, user can start running the assembly (editing is locked)
  ASSEMBLED: 'ASSEMBLED', // After assembly has finished (editing remains locked; could reset)
};

export enum EndpointType {
  ENZYME = 'ENZYME',
  HOMOLOGY = 'HOMOLOGY',
  GIBSON = 'GIBSON',
  CONCATENATION = 'CONCATENATION',
}

export const LOCKED_ASSEMBLY_STATES = [BULK_ASSEMBLY_STATE.ASSEMBLING, BULK_ASSEMBLY_STATE.ASSEMBLED];
export const BULK_ASSEMBLY_FINALIZE_IDX = -1;

export const BULK_ASSEMBLY_FILE_FILTER: Array<Filter> = [
  {
    key: FilterKey.TYPES,
    operator: Operator.IS_ONE_OF,
    value: [ModelTypeConstants.SEQUENCE, ModelTypeConstants.DNA_OLIGO],
  },
];

export const BULK_ASSEMBLY_FOLDER_FILTER: FileSelectorFolderFilter = {
  level: 'read',
  includeHiddenProjects: false,
  showInventory: true,
  showNotebook: false,
  showRegistries: true,
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const ENDPOINT_TYPE_DISPLAY_NAMES: any = {
  [EndpointType.ENZYME]: 'Enzyme',
  [EndpointType.HOMOLOGY]: 'Homology',
  [EndpointType.GIBSON]: 'Gibson',
  [EndpointType.CONCATENATION]: 'Concatenation',
};

export const ENDPOINT_TYPE_DESCRIPTIONS: {[key in EndpointType]: string} = {
  [EndpointType.ENZYME]: 'Attach at a unique enzyme site',
  [EndpointType.HOMOLOGY]: 'Attach at an existing shared homology region',
  [EndpointType.GIBSON]: 'Create primers using the Gibson protocol',
  [EndpointType.CONCATENATION]: 'Concatenate two fragments',
};

export const ENDPOINT_TYPE_ICONS: {[key in EndpointType]: BenchlingIconName} = {
  [EndpointType.ENZYME]: 'enzyme',
  [EndpointType.HOMOLOGY]: 'homology',
  [EndpointType.GIBSON]: FolderItemIcon.PrimerPair,
  [EndpointType.CONCATENATION]: 'concatenation',
};

export const warningText =
  'The following issues will not prevent you from performing the assembly, but may prevent you from registering your assembled sequences.';

export const OVERLAP_WINDOW = 10;

export const defaultGibsonParameters: GibsonParameters = {
  tmAnneal: 50,
  tmWhole: 60,
  tmDiff: 5,
  minOverlap: 20,
  maxOverlap: 20 + OVERLAP_WINDOW,
};
